// How a refused dispatch READS (#1034, #1100). `lib/task-dispatch.ts` owns the
// POST and the verdict; this owns the one thing that changes for a different
// reason — the copy a refusal is framed with.
//
// A refusal answers on two channels: `detail` is the sentence a human reads,
// `extensions` the same reason as data. TWO extensions earn a line here, both on
// the 429: `max_depth`, which says the refusal is the dispatch queue's
// backpressure and not the API's rate limiter, and then `retry_after`, which
// turns "try again shortly" into the wait the server actually asked for.
// `capability` (403) and `sink` (501) are already spelled out inside `detail`;
// restating them would print one fact twice.

import type { DispatchOutcome } from './task-dispatch';

/** The one outcome every non-2xx collapses into. */
export type RefusedOutcome = Extract<DispatchOutcome, { kind: 'refused' }>;

/**
 * Copy key framing a refusal, for the statuses whose STATUS is the whole reason.
 * An unmapped one (or a dead network, 0) is generic — and so is a 429, which two
 * different causes answer with: see `refusalCopy`.
 */
const REFUSAL_KEYS: Record<number, string> = {
  403: 'fleet.new_task.result_refused',
  404: 'fleet.new_task.result_repo_gone',
  501: 'fleet.new_task.result_unavailable',
  502: 'fleet.new_task.result_dispatch_failed',
};

export const refusalKey = (status: number): string =>
  REFUSAL_KEYS[status] ?? 'fleet.new_task.result_error';

/**
 * A copy key plus the numbers it interpolates — `t(key, vars)`, or, when it
 * carries a `count`, a plural family rendered by `tPlural(key, count, vars)`.
 */
export type RefusalCopy = {
  readonly key: string;
  readonly vars?: Readonly<Record<string, number>>;
  readonly count?: number;
};

/**
 * Is this 429 the dispatch queue's backpressure, or the API throttling the
 * caller? TWO middlewares answer a dispatch with 429 — the queue-depth guard
 * (`apps/api/src/routes/tasks.ts`) and the per-IP/per-token rate limiter
 * (`apps/api/src/middleware/auth.ts`) — and both send a `retry_after`, so the
 * status alone names no cause. Only the queue guard reports the depth it hit,
 * so `max_depth` is the discriminator. Not the problem `type`
 * (`…/errors/queue-full`), which would be the plainest one: it is a STANDARD
 * problem member, so `problemExtensions` filters it out and `ApiError` never
 * sees it.
 */
const isQueueFull = (refused: RefusedOutcome): boolean =>
  refused.status === 429 && typeof refused.extensions?.['max_depth'] === 'number';

/**
 * The line a refusal renders. Anything but a queue-full 429 is framed by status
 * alone, and `<NewTaskRefused/>` prints the server's own `detail` under it — so a
 * throttled caller reads "couldn't dispatch" + "rate limit exceeded", never a
 * queue that isn't full.
 *
 * A queue-full one spends its `retry_after` (seconds, sent beside the
 * `Retry-After` header): the operator is told how long to wait instead of
 * guessing at "shortly". A missing, non-numeric, non-finite or non-positive
 * value falls back to exactly today's copy — a wait we can't name beats "try
 * again in NaN seconds".
 */
export function refusalCopy(refused: RefusedOutcome): RefusalCopy {
  if (!isQueueFull(refused)) return { key: refusalKey(refused.status) };
  const raw = refused.extensions?.['retry_after'];
  if (typeof raw !== 'number' || !Number.isFinite(raw) || raw <= 0) {
    return { key: 'fleet.new_task.result_queue_full' };
  }
  const seconds = Math.ceil(raw);
  return { key: 'fleet.new_task.result_queue_full_in', count: seconds, vars: { seconds } };
}
